/**
 * Versiebewuste cache (plan 17 §7, E4; ADR-007).
 *
 * Elk cachebaar antwoord van de API draagt de dataversie van de tenant in de
 * kop `X-Atlas-Data-Version` (E2, A5). Die versie gaat hier in een kleine
 * store, en elk antwoord onthoudt onder welke versie het binnenkwam (een
 * onzichtbaar veld op de data, gezet in `api/client.ts`). Daarmee:
 *
 * - **bewaren op versie in plaats van op tijd**: een geversioneerde query is
 *   nooit verouderd zolang er geen nieuwere versie is (`staleTime: Infinity`),
 *   en wordt ververst zodra er wel een is (`installVersionInvalidation`).
 *   Opnieuw vragen kost dan een 304 (E3);
 * - **een zichtbare markering**: een getal staat alleen zonder markering in
 *   beeld als zijn versie de laatst bevestigde is, en die bevestiging jonger
 *   is dan 60 s. Anders "wordt bijgewerkt" of "niet gecontroleerd of dit
 *   actueel is" (`freshness`, `DataVersionStatus`).
 *
 * De bevestiging komt uit elk antwoord met de kop, en uit `GET /data-version`
 * die de schil elke 20 s vraagt zolang het tabblad zichtbaar is.
 */
import { type Query, type QueryClient, replaceEqualDeep } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

export const DATA_VERSION_HEADER = "x-atlas-data-version";

/** Hoe lang een bevestiging van de versie geldt voordat de markering verschijnt. */
export const CONFIRM_WINDOW_MS = 60_000;

/** Hoe vaak de schil de versie navraagt (alleen bij een zichtbaar tabblad). */
export const POLL_MS = 20_000;

/** Hoe lang een geversioneerd antwoord in het geheugen blijft na het laatste gebruik. */
export const VERSIONED_GC_MS = 30 * 60_000;

const VERSION = Symbol("atlasDataVersion");

export type DataVersionState = {
  /** De hoogste versie die de API heeft gemeld. */
  latest: number | null;
  /** Wanneer de API die versie voor het laatst bevestigde (ms sinds epoch). */
  confirmedAt: number | null;
};

let state: DataVersionState = { latest: null, confirmedAt: null };
const listeners = new Set<() => void>();

function publish(next: DataVersionState): void {
  state = next;
  for (const listener of listeners) listener();
}

/** Een antwoord meldt versie `version` op tijdstip `at`. */
export function observeDataVersion(version: number, at: number = Date.now()): void {
  if (!Number.isFinite(version)) return;
  if (state.latest === null || version >= state.latest) {
    publish({ latest: version, confirmedAt: at });
  }
}

/** Leest de versie uit een kopwaarde; geen of een onleesbare kop is geen versie. */
export function parseVersionHeader(value: unknown): number | undefined {
  if (typeof value !== "string" || value === "") return undefined;
  const version = Number(value);
  return Number.isSafeInteger(version) ? version : undefined;
}

/** Hangt de versie onzichtbaar aan een antwoord (niet in JSON, niet in een vergelijking). */
export function tagWithVersion<T>(data: T, version: number): T {
  if (data !== null && typeof data === "object" && !Object.isFrozen(data)) {
    Object.defineProperty(data, VERSION, { value: version, enumerable: false, configurable: true });
  }
  return data;
}

/** De versie waaronder een antwoord binnenkwam, of undefined als het niet geversioneerd is. */
export function versionOf(data: unknown): number | undefined {
  if (data === null || typeof data !== "object") return undefined;
  const version = (data as Record<symbol, unknown>)[VERSION];
  return typeof version === "number" ? version : undefined;
}

export type Freshness = "current" | "updating" | "unconfirmed";

/**
 * Hoe actueel een getal met versie `dataVersion` is.
 *
 * - `unconfirmed`: de laatste bevestiging is ouder dan 60 s (of er is er nog geen);
 * - `updating`: er is een nieuwere versie dan die van het getal;
 * - `current`: de versie van het getal is de laatst bevestigde, en recent.
 */
export function freshness(s: DataVersionState, dataVersion: number | undefined, now: number): Freshness {
  if (s.latest === null || s.confirmedAt === null || now - s.confirmedAt > CONFIRM_WINDOW_MS) {
    return "unconfirmed";
  }
  if (dataVersion !== undefined && dataVersion < s.latest) return "updating";
  return "current";
}

/** Een geversioneerde query is verouderd zodra er een nieuwere versie is, niet na een tijd. */
export function versionedStaleTime(fallbackMs: number) {
  return (query: Query): number => (versionOf(query.state.data) !== undefined ? Infinity : fallbackMs);
}

/**
 * Structural sharing dat de versie meeneemt. React Query houdt bij een
 * inhoudelijk gelijk antwoord het oude object vast (minder renders), maar dat
 * object draagt de oude versie; zonder dit zou een ongewijzigd scherm na een
 * nieuwe versie blijven melden dat het wordt bijgewerkt.
 */
export function versionedStructuralSharing(oldData: unknown, newData: unknown): unknown {
  const merged = replaceEqualDeep(oldData, newData);
  const version = versionOf(newData);
  return version !== undefined && merged !== newData ? tagWithVersion(merged, version) : merged;
}

/** Een geversioneerde query van vóór de laatste versie. */
export function isOutdated(query: Query, latest: number | null = state.latest): boolean {
  const version = versionOf(query.state.data);
  return version !== undefined && latest !== null && version < latest;
}

/**
 * Ververst alle geversioneerde queries van een oudere versie zodra er een
 * nieuwere binnenkomt. Actieve queries vragen dan opnieuw (een 304 als hun
 * antwoord toch niet veranderde), de rest bij het volgende gebruik.
 */
export function installVersionInvalidation(client: QueryClient): () => void {
  let seen = state.latest;
  const listener = () => {
    if (state.latest === null || state.latest === seen) return;
    seen = state.latest;
    void client.invalidateQueries({ predicate: (query) => isOutdated(query) });
  };
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDataVersion(): DataVersionState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Alleen voor toetsen en na uitloggen: terug naar "niets bekend". */
export function resetDataVersion(): void {
  publish({ latest: null, confirmedAt: null });
}
